import { inject, Injectable } from '@angular/core';
import { ProductNames } from '@mynexus/mynexus-ui-lib';
import { SelectItem } from 'primeng/api';
import { firstValueFrom, lastValueFrom, map } from 'rxjs';
import { DocumentEntity, DocumentReference } from '../enumerations';
import { IDocumentReferenceProductDTO, InboxDto, ReferenceDto, Users } from '../model';
import { DocumentReferenceApiService } from './document-reference-api.service';
import { GraphApiService } from './graph-api.service';
import { LookupApiService } from './lookup-api.service';
import { NotificationService } from './notification.service';

@Injectable()
export class LookupService {
  private readonly lookupApiService = inject(LookupApiService);
  private readonly graphApiService = inject(GraphApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly documentReferenceApiService = inject(DocumentReferenceApiService);

  private inboxesCache: InboxDto[] = [];
  private faxFolderReferenceCache: Record<string, ReferenceDto[]> = {};
  private statusReferenceCache: ReferenceDto[] = [];
  private requestTypeReferenceCache: Record<string, ReferenceDto[]> = {};
  private archivedReasonReferenceCache: ReferenceDto[] = [];
  private usersCache: SelectItem[] = [];
  private documentTypeCache: Record<
    string,
    { auth: IDocumentReferenceProductDTO[]; line: IDocumentReferenceProductDTO[] }
  > = {};

  public async getInboxes(): Promise<InboxDto[]> {
    if (this.inboxesCache.length > 0) {
      return this.inboxesCache;
    }

    let options: InboxDto[] = [];
    try {
      options = await firstValueFrom(this.lookupApiService.getAllInboxes());
    } catch {
      this.notificationService.showError(
        'Unable to Load Inboxes',
        'There was an issue loading the inboxes. Please try again later.'
      );
    }

    this.inboxesCache = options;
    return this.inboxesCache;
  }

  public async getFaxFolderReferences(inbox: InboxDto): Promise<ReferenceDto[]> {
    if (this.faxFolderReferenceCache[inbox.applicationKey]) {
      return this.faxFolderReferenceCache[inbox.applicationKey];
    }

    let options: ReferenceDto[] = [];
    try {
      options = await lastValueFrom(this.lookupApiService.getFaxFolderReferences(inbox.applicationKey));
    } catch {
      this.notificationService.showError(
        'Unable to Load Fax Folders',
        'There was an issue loading the fax folders. Please try again later.'
      );
    }

    this.faxFolderReferenceCache = { [inbox.applicationKey]: options };
    return this.faxFolderReferenceCache[inbox.applicationKey];
  }

  public async getStatusReferences(): Promise<ReferenceDto[]> {
    if (this.statusReferenceCache.length > 0) {
      return this.statusReferenceCache;
    }

    let options: ReferenceDto[] = [];
    try {
      options = await lastValueFrom(this.lookupApiService.getStatusReferences());
    } catch {
      this.notificationService.showError(
        'Unable to Load Statuses',
        'There was an issue loading the statuses. Please try again later.'
      );
    }

    this.statusReferenceCache = options;
    return this.statusReferenceCache;
  }

  public async getRequestTypeReferences(inbox: InboxDto): Promise<ReferenceDto[]> {
    if (this.requestTypeReferenceCache[inbox.applicationKey]) {
      return this.requestTypeReferenceCache[inbox.applicationKey];
    }

    let options: ReferenceDto[] = [];
    try {
      options = await lastValueFrom(
        this.lookupApiService.getRequestTypeReferences(inbox.tenantLobProduct?.tenantLobProductId || null)
      );
    } catch {
      this.notificationService.showError(
        'Unable to Load Request Types',
        'There was an issue loading the request types. Please try again later.'
      );
    }

    this.requestTypeReferenceCache = {
      [inbox.applicationKey]: options,
    };
    return this.requestTypeReferenceCache[inbox.applicationKey];
  }

  public async getArchivedReasonReferences(): Promise<ReferenceDto[]> {
    if (this.archivedReasonReferenceCache.length > 0) {
      return this.archivedReasonReferenceCache;
    }

    let options: ReferenceDto[] = [];
    try {
      options = await lastValueFrom(this.lookupApiService.getArchivedReasonReferences());
    } catch {
      this.notificationService.showError(
        'Unable to Load Archived Reasons',
        'There was an issue loading the archived reasons. Please try again later.'
      );
    }

    this.archivedReasonReferenceCache = options;
    return this.archivedReasonReferenceCache;
  }

  // As per requirement, the filter will not be consistent with the modal dropdown, so will not be cached
  // like it's modal counterpart.
  public async getAssignedUsersFilter(inbox: InboxDto): Promise<SelectItem[]> {
    return await lastValueFrom(this.lookupApiService.getAssignedUsersFilter(inbox.inboxId));
  }

  public async getUsersDropdown(currentUserId: string): Promise<SelectItem[]> {
    if (this.usersCache.length > 0) {
      return this.usersCache;
    }

    // Get all Users. Sort them and move current User at the top of the list
    let users: Users[] = [];
    try {
      users = await lastValueFrom(this.graphApiService.getAllUsers().pipe(map((x) => x.items)));
    } catch {
      this.notificationService.showError(
        'Unable to Load Users',
        'There was an issue loading the users. Please try again later.'
      );
    }

    if (!users?.length) {
      return [];
    }

    const sortedUsers = users.sort((a, b) => a.firstName.localeCompare(b.firstName));
    const currentUserIndex = sortedUsers.findIndex((user) => user.userId === currentUserId);

    if (currentUserIndex !== -1) {
      const currentUser = sortedUsers.splice(currentUserIndex, 1)[0];
      currentUser.displayName += ' (me)';
      sortedUsers.unshift(currentUser);
    }

    this.usersCache = sortedUsers.map((x) => ({
      label: x.displayName,
      value: x.userId,
    }));

    return this.usersCache;
  }

  public async getDocumentTypes(
    inbox: InboxDto
  ): Promise<{ auth: IDocumentReferenceProductDTO[]; line: IDocumentReferenceProductDTO[] }> {
    // There are no document types for tenantless inboxes
    if (!inbox.tenantLobProduct) {
      return { auth: [], line: [] };
    }

    if (this.documentTypeCache[inbox.applicationKey]) {
      return this.documentTypeCache[inbox.applicationKey];
    }

    let auth: IDocumentReferenceProductDTO[] = [];
    let line: IDocumentReferenceProductDTO[] = [];
    try {
      const product = inbox.tenantLobProduct.productShortName;

      // PAC has to fetch auth and line types separately. Other programs can reuse the same value.
      // TODO: Remove PAC Commercial after LOB migration
      if (product === ProductNames.PACIM || product === ProductNames.PacCommercial) {
        auth = await lastValueFrom(
          this.documentReferenceApiService.getDocumentTypes(
            inbox.tenantLobProduct.tenantLobProductId,
            DocumentReference.DocumentType,
            DocumentEntity.Auth
          )
        );

        line = await lastValueFrom(
          this.documentReferenceApiService.getDocumentTypes(
            inbox.tenantLobProduct.tenantLobProductId,
            DocumentReference.DocumentType,
            DocumentEntity.Line
          )
        );
      } else {
        auth = await lastValueFrom(
          this.documentReferenceApiService.getDocumentTypes(
            inbox.tenantLobProduct.tenantLobProductId,
            DocumentReference.Upload,
            DocumentEntity.None
          )
        );
        line = auth;
      }
    } catch {
      this.notificationService.showError(
        'Unable to Load Document Types',
        'There was an issue loading the document types. Please try again later.'
      );
    }

    this.documentTypeCache[inbox.applicationKey] = {
      auth,
      line,
    };
    return this.documentTypeCache[inbox.applicationKey];
  }
}
